/**
 * Layout Template Schema for Flow2
 *
 * This file defines the structural layout layer that positions content blocks.
 * Layouts are templates with slots that content blocks are assigned to.
 *
 * @packageDocumentation
 * @module ai/schemas
 */

import { ContentBlockType, ScreenType } from './ContentBlock';

/**
 * Position and size unit types
 */
export type PositionUnit = 'px' | '%';

/**
 * Frame definition with position and size
 */
export interface LayoutFrame {
  /** X position (from left) */
  x: number;
  /** Y position (from top) */
  y: number;
  /** Width */
  width: number;
  /** Height */
  height: number;
  /** Unit for position values (default: px) */
  unit?: PositionUnit;
}

/**
 * Constraints for content within a slot
 */
export interface SlotConstraints {
  /** Maximum number of text lines */
  maxLines?: number;
  /** Minimum font size allowed */
  minFontSize?: number;
  /** Maximum font size allowed */
  maxFontSize?: number;
  /** Maximum character count */
  maxChars?: number;
  /** Whether text should auto-fit to slot */
  autoFit?: boolean;
  /** Padding inside the slot */
  padding?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  };
}

/**
 * Style overrides specific to a slot
 */
export interface SlotStyleOverrides {
  /** Font size override */
  fontSize?: number;
  /** Font name override */
  fontName?: string;
  /** Text alignment */
  textAlignment?: 'left' | 'center' | 'right';
  /** Text color (hex or CSS color) */
  textColor?: string;
  /** Background color */
  backgroundColor?: string;
  /** Background opacity (0-1) */
  backgroundOpacity?: number;
  /** Border radius */
  borderRadius?: number;
  /** Line height multiplier */
  lineHeight?: number;
  /** Letter spacing */
  letterSpacing?: number;
}

/**
 * A slot within a layout where content blocks can be placed
 */
export interface LayoutSlot {
  /** Unique identifier for this slot */
  id: string;
  /** Human-readable name for the slot */
  name: string;
  /** Content block types that can be placed in this slot */
  acceptedTypes: ContentBlockType[];
  /** Position and size of the slot */
  frame: LayoutFrame;
  /** Constraints for content in this slot */
  constraints?: SlotConstraints;
  /** Default style overrides for this slot */
  defaultStyle?: SlotStyleOverrides;
  /** Whether this slot is required */
  required?: boolean;
  /** Priority for auto-assignment (higher = more important) */
  priority?: number;
  /** Z-index for layering */
  zIndex?: number;
}

/**
 * Rules for selecting this layout
 */
export interface LayoutSelectionRules {
  /** Screen types this layout works well with */
  preferredScreenTypes?: ScreenType[];
  /** Minimum number of content blocks needed */
  minBlocks?: number;
  /** Maximum number of content blocks supported */
  maxBlocks?: number;
  /** Required block types for this layout */
  requiredBlockTypes?: ContentBlockType[];
  /** Score modifier based on content match */
  scoreBonus?: number;
}

/**
 * Layout template categories
 */
export type LayoutCategory =
  | 'title'
  | 'content'
  | 'comparison'
  | 'media'
  | 'list'
  | 'quote'
  | 'data'
  | 'closing';

/**
 * Layout template definition
 */
export interface LayoutTemplate {
  /** Unique identifier for this layout */
  id: string;
  /** Human-readable name */
  name: string;
  /** Category of layout */
  category: LayoutCategory;
  /** Description of the layout */
  description?: string;
  /** Slots where content can be placed */
  slots: LayoutSlot[];
  /** Rules for selecting this layout */
  selectionRules?: LayoutSelectionRules;
  /** Thumbnail preview URL */
  thumbnailUrl?: string;
  /** Whether this is a system layout or user-created */
  isSystem?: boolean;
  /** Tags for searching/filtering */
  tags?: string[];
}

/**
 * Standard view sizes for FlowVella presentations
 */
export const VIEW_SIZES = {
  /** iPad / Standard 4:3-ish aspect ratio */
  STANDARD: { width: 1024, height: 748 },
  /** Desktop widescreen 16:9 */
  WIDESCREEN: { width: 1920, height: 1080 },
} as const;

/**
 * Default view size for FlowVella presentations
 * Now defaults to widescreen for modern desktop experience
 */
export const DEFAULT_VIEW_SIZE = VIEW_SIZES.WIDESCREEN;

/**
 * Legacy view size (for backwards compatibility)
 */
export const LEGACY_VIEW_SIZE = VIEW_SIZES.STANDARD;

/**
 * Common padding values
 */
export const LAYOUT_PADDING = {
  screen: 40,
  element: 20,
  text: 10,
} as const;

/**
 * Utility function to convert percentage frame to pixels
 */
export function frameToPixels(
  frame: LayoutFrame,
  viewSize: { width: number; height: number } = DEFAULT_VIEW_SIZE
): LayoutFrame {
  if (frame.unit !== '%') {
    return frame;
  }
  return {
    x: (frame.x / 100) * viewSize.width,
    y: (frame.y / 100) * viewSize.height,
    width: (frame.width / 100) * viewSize.width,
    height: (frame.height / 100) * viewSize.height,
    unit: 'px',
  };
}

/**
 * Utility function to create a layout slot
 */
export function createSlot(
  id: string,
  name: string,
  acceptedTypes: ContentBlockType[],
  frame: LayoutFrame,
  options?: {
    constraints?: SlotConstraints;
    defaultStyle?: SlotStyleOverrides;
    required?: boolean;
    priority?: number;
    zIndex?: number;
  }
): LayoutSlot {
  return {
    id,
    name,
    acceptedTypes,
    frame,
    constraints: options?.constraints,
    defaultStyle: options?.defaultStyle,
    required: options?.required ?? false,
    priority: options?.priority ?? 0,
    zIndex: options?.zIndex ?? 0,
  };
}

/**
 * Utility function to create a layout template
 */
export function createLayoutTemplate(
  id: string,
  name: string,
  category: LayoutCategory,
  slots: LayoutSlot[],
  options?: {
    description?: string;
    selectionRules?: LayoutSelectionRules;
    tags?: string[];
  }
): LayoutTemplate {
  return {
    id,
    name,
    category,
    slots,
    description: options?.description,
    selectionRules: options?.selectionRules,
    isSystem: true,
    tags: options?.tags,
  };
}

/**
 * Calculate slot score for a given content block type
 * Higher score means better fit
 */
export function calculateSlotScore(
  slot: LayoutSlot,
  blockType: ContentBlockType
): number {
  // Check if type is accepted
  if (!slot.acceptedTypes.includes(blockType)) {
    return -1;
  }

  let score = 1;

  // Bonus for matching priority
  score += (slot.priority || 0) * 0.1;

  // Bonus if slot is required and block type matches
  if (slot.required) {
    score += 0.5;
  }

  // Bonus for exact type match (not just compatible)
  if (slot.acceptedTypes.length === 1 && slot.acceptedTypes[0] === blockType) {
    score += 0.3;
  }

  return score;
}

/**
 * Calculate layout score for a given screen content
 */
export function calculateLayoutScore(
  layout: LayoutTemplate,
  blockTypes: ContentBlockType[],
  screenType: ScreenType
): number {
  let score = 0;
  const rules = layout.selectionRules;

  // Check preferred screen types
  if (rules?.preferredScreenTypes?.includes(screenType)) {
    score += 2;
  }

  // Check block count
  const blockCount = blockTypes.length;
  if (rules?.minBlocks && blockCount < rules.minBlocks) {
    return -1; // Not enough blocks
  }
  if (rules?.maxBlocks && blockCount > rules.maxBlocks) {
    return -1; // Too many blocks
  }

  // Check required block types
  if (rules?.requiredBlockTypes) {
    const hasRequired = rules.requiredBlockTypes.every((type) =>
      blockTypes.includes(type)
    );
    if (!hasRequired) {
      return -1;
    }
    score += 1;
  }

  // Check slot coverage
  let coveredSlots = 0;
  for (const slot of layout.slots) {
    for (const blockType of blockTypes) {
      if (slot.acceptedTypes.includes(blockType)) {
        coveredSlots++;
        break;
      }
    }
  }

  // Score based on slot utilization
  const utilization = coveredSlots / layout.slots.length;
  score += utilization * 2;

  // Add any score bonus from rules
  if (rules?.scoreBonus) {
    score += rules.scoreBonus;
  }

  return score;
}

/**
 * Assignment of a content block to a layout slot
 */
export interface SlotAssignment {
  slotId: string;
  blockId: string;
  blockType: ContentBlockType;
}

/**
 * Result of layout compilation for a screen
 */
export interface LayoutCompilationResult {
  layoutId: string;
  assignments: SlotAssignment[];
  unassignedBlocks: string[];
  unusedSlots: string[];
  warnings: string[];
  blockMappings?: { blockId: string; mmoIds: string[] }[];
}

/**
 * Current schema version for layouts
 */
export const LAYOUT_SCHEMA_VERSION = '1.0.0';
